import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { INTL_DAY_MONTH_NAME_SHORT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { rem } from 'polished'
import { sum } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import moment from 'moment'
import { useIntl } from 'react-intl'

const SizedFilterSelectBox = styled(OfferFilterDropdownBox)`
  ${mediaQueryUp.desktop} {
    min-width: ${rem(250)};
  }
`

interface Props {
  bundledOffersState: OfferPageState['bundledOffers']
  checkIn?: string
  selectedDuration: number
  activeFilter: string
  inSidebar?: boolean
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  iconDisabled?: boolean
  roomTextDisabled?: boolean
  ref?: React.Ref<HTMLButtonElement | HTMLDivElement>
  charterBundleRoute?: App.CharterBundleRoute
}

function OfferCalendarDatePickerSelectBox(props: Props) {
  const {
    checkIn,
    selectedDuration,
    activeFilter,
    inSidebar,
    bundledOffersState,
    onClick = noop,
    iconDisabled,
    roomTextDisabled,
    ref,
    charterBundleRoute,
  } = props

  const bundledOffers = useMemo(() => Object.values(bundledOffersState ?? {}).filter(Boolean), [bundledOffersState])

  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()

  const { dates, duration } = useMemo(() => {
    if (bundledOffers.length > 0) {
      return {
        dates: bundledOffers.map((bundledOffer) => ({
          checkIn: bundledOffer.checkInDate,
          checkOut: bundledOffer.checkInDate ? moment(bundledOffer.checkInDate).add(bundledOffer.duration, 'days') : undefined,
        })),
        duration: sum(bundledOffers, (s) => {
          if (s.checkInDate) {
            return s.duration ?? 0
          }
          return 0
        }),
      }
    }

    // For charter flights, show flight dates (departure is day before check-in, return is checkout day)
    if (charterBundleRoute && checkIn) {
      const checkOut = moment(checkIn).add(selectedDuration, 'days').format(ISO_DATE_FORMAT)
      return {
        dates: [{
          checkIn: moment(checkIn).subtract(1, 'day').format(ISO_DATE_FORMAT),
          checkOut,
        }],
        duration: selectedDuration,
      }
    }

    return {
      dates: [{
        checkIn,
        checkOut: checkIn ? moment(checkIn).add(selectedDuration, 'days').format(ISO_DATE_FORMAT) : undefined,
      }],
      duration: selectedDuration,
    }
  }, [bundledOffers, checkIn, selectedDuration, charterBundleRoute])

  const value = useMemo(() => {
    const validDates = dates.filter((d) => d.checkIn)
    if (validDates.length) {
      const dateText = validDates.map((d) => `${intlDateFormatter(d.checkIn!, INTL_DAY_MONTH_NAME_SHORT)} - ${intlDateFormatter(d.checkOut!, INTL_DAY_MONTH_NAME_SHORT)}`).join(', ')
      const roomText = roomTextDisabled
        ? undefined
        : intl.formatMessage(
            {
              id: 'J4HtvSjuZX',
              defaultMessage: '({count, plural, one {# night} other {# nights}})',
              description: 'Parenthesized night count for date selection display',
            },
            { count: duration },
          )
      return <><b>{dateText}</b> {roomText}</>
    }
  }, [dates, duration, intlDateFormatter, roomTextDisabled, intl])

  const dispatch = useOfferPageDispatch()
  const isOpen = activeFilter === 'checkin'

  const onSelectClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    if (isOpen) {
      dispatch({ type: OfferPageActions.closeFilter })
    } else {
      dispatch({ type: OfferPageActions.setRoomType, room: undefined })
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
    onClick(e)
  }, [dispatch, isOpen, onClick])

  return (
    <SizedFilterSelectBox
      ref={ref}
      inSidebar={inSidebar}
      onClick={onSelectClick}
      active={isOpen}
      icon={!iconDisabled && <LineCalendarIcon />}
      data-testid="OfferCalendarDatePickerSelectBox"
      label={intl.formatMessage({
        id: 'W/7jAdEEow',
        defaultMessage: 'When',
        description: 'Label for the date picker filter input in the search and filtering interface',
      })}
      placeholder={intl.formatMessage({
        id: '/RtJ/7Vnid',
        defaultMessage: 'Select your dates',
        description: 'Placeholder text for date picker filter in the search or listing filters panel',
      })}
      value={value}
    />
  )
}

export default withOfferPageState((state) => {
  return {
    bundledOffersState: state.bundledOffers,
    checkIn: state.checkInDate,
    selectedDuration: state.duration,
    activeFilter: state.activeFilter,
    offer: state.offer,
    charterBundleRoute: state.charterBundleRoute,
  }
}, OfferCalendarDatePickerSelectBox)
